/* Skinoria — spin-to-win wheel.
 *
 * One spin per browser per cooldown window (24h by default). State lives in
 * localStorage under du_wheel_v1:
 *   { at: <ms timestamp of the spin>, prize: { label, code, message } }
 *
 * During the cooldown the visitor can still open the wheel — they see the prize
 * they already won and how long until the next spin, rather than a dead panel.
 *
 * The wheel never decides a price. A winning segment reveals a discount code
 * that already exists in Shopify; Shopify validates it at checkout.
 */
(function () {
  'use strict';

  var KEY = 'du_wheel_v1';
  var HOUR = 3600 * 1000;

  function readState() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      return (s && typeof s.at === 'number') ? s : null;
    } catch (e) { return null; }
  }

  function writeState(s) {
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }

  function formatLeft(ms) {
    var mins = Math.max(0, Math.ceil(ms / 60000));
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    return m + ' minute' + (m === 1 ? '' : 's');
  }

  customElements.define('du-spin-wheel', class extends HTMLElement {
    connectedCallback() {
      this.modal    = this.querySelector('.du-wheel');
      this.tab      = this.querySelector('.du-wheel-tab');
      this.disc     = this.querySelector('[data-disc]');
      this.spinBtn  = this.querySelector('[data-spin]');
      this.stage    = this.querySelector('[data-stage]');
      this.result   = this.querySelector('[data-result]');
      this.fine     = this.querySelector('[data-fine]');
      this.cooldown = (parseInt(this.dataset.cooldown, 10) || 24) * HOUR;
      this.turns    = 0;
      this.rotation = 0;
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      try {
        this.prizes = JSON.parse(this.querySelector('[data-prizes]').textContent);
      } catch (e) { this.remove(); return; }
      if (!this.prizes.length) { this.remove(); return; }
      this.segments = parseInt(this.dataset.segments, 10) || this.prizes.length;

      this.tab.hidden = false;
      this.tab.addEventListener('click', this.open.bind(this));
      this.querySelectorAll('[data-close]').forEach(function (el) {
        el.addEventListener('click', this.close.bind(this));
      }, this);
      this.spinBtn.addEventListener('click', this.spin.bind(this));
      this.onKey = function (e) { if (e.key === 'Escape' && !this.modal.hidden) this.close(); }.bind(this);
      document.addEventListener('keydown', this.onKey);

      // Auto-open only when they are actually allowed to spin, and only once
      // per page session so it never nags.
      if (this.eligible() && !sessionStorage.getItem(KEY + '_seen')) {
        var delay = (parseInt(this.dataset.delay, 10) || 20) * 1000;
        this.timer = setTimeout(this.open.bind(this), delay);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      clearTimeout(this.timer);
    }

    remaining() {
      var s = readState();
      if (!s) return 0;
      return Math.max(0, s.at + this.cooldown - Date.now());
    }

    eligible() { return this.remaining() === 0; }

    open() {
      clearTimeout(this.timer);
      try { sessionStorage.setItem(KEY + '_seen', '1'); } catch (e) {}
      this.modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';

      if (!this.eligible()) {
        // Still inside the cooldown — show what they already won.
        var s = readState();
        this.showResult(s && s.prize ? s.prize : null, true);
      } else {
        this.spinBtn.focus();
      }
    }

    close() {
      clearTimeout(this.timer);
      this.modal.hidden = true;
      document.documentElement.style.overflow = '';
    }

    transitionDisc(angle, duration, easing) {
      return new Promise(function (resolve) {
        var complete = false;
        var finish = function (event) {
          if (event && (event.target !== this.disc || event.propertyName !== 'transform')) return;
          if (complete) return;
          complete = true;
          this.disc.removeEventListener('transitionend', finish);
          resolve();
        }.bind(this);

        this.disc.addEventListener('transitionend', finish);
        this.disc.style.transition = 'transform ' + duration + 'ms ' + easing;
        requestAnimationFrame(function () {
          this.disc.style.transform = 'rotate(' + angle + 'deg)';
        }.bind(this));
        setTimeout(finish, duration + 120);
      }.bind(this));
    }

    wait(ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    }

    /* Weighted pick, so a rare prize can sit on the wheel without being as
     * likely as everything else. A segment's "weight" is relative: all at 10 is
     * even odds; a segment at 1 is ten times less likely than one at 10; 0 means
     * it is displayed but never lands. Falls back to an even pick if no weights
     * are set at all. */
    pickIndex() {
      var weights = this.prizes.map(function (p) {
        var w = Number(p && p.weight);
        return (isFinite(w) && w >= 0) ? w : 10;
      });
      var total = weights.reduce(function (a, b) { return a + b; }, 0);
      if (!(total > 0)) return Math.floor(Math.random() * this.prizes.length);
      var roll = Math.random() * total;
      for (var i = 0; i < weights.length; i++) {
        roll -= weights[i];
        if (roll < 0) return i;
      }
      return weights.length - 1;
    }

    async spin() {
      if (this.spinning || !this.eligible()) return;
      this.spinning = true;
      this.spinBtn.disabled = true;
      this.disc.classList.add('is-spinning');

      var index = this.pickIndex();
      var prize = this.prizes[index];
      var segAngle = 360 / this.segments;

      // Land the middle of the chosen wedge under the pointer at 12 o'clock,
      // with a small random offset so it never stops in the exact same spot.
      var jitter = (Math.random() - 0.5) * (segAngle * 0.5);
      this.turns += 6 + Math.floor(Math.random() * 3);
      var target = this.turns * 360 - (index * segAngle + segAngle / 2) + jitter;

      // Record the spin immediately, so a refresh mid-animation cannot be used
      // to re-roll a losing result.
      writeState({ at: Date.now(), prize: prize });

      if (this.reducedMotion) {
        this.disc.style.transition = 'none';
        this.disc.style.transform = 'rotate(' + target + 'deg)';
        await this.wait(80);
      } else {
        // A slight wind-back makes the wheel feel weighted, followed by a long
        // deceleration and a quiet two-step mechanical settle at the pointer.
        await this.transitionDisc(this.rotation - 9, 280, 'cubic-bezier(.45,0,.75,.35)');
        await this.transitionDisc(target + 2.4, 5100, 'cubic-bezier(.1,.68,.12,1)');
        await this.transitionDisc(target - .9, 170, 'cubic-bezier(.3,0,.7,1)');
        await this.transitionDisc(target, 240, 'cubic-bezier(.2,.8,.25,1)');
      }

      this.rotation = target;
      this.disc.style.transition = 'none';
      this.disc.classList.remove('is-spinning');
      this.disc.style.setProperty('--win-start', (index * segAngle) + 'deg');
      this.disc.style.setProperty('--win-end', ((index + 1) * segAngle) + 'deg');
      this.disc.classList.add('is-winner');

      var labels = this.disc.querySelectorAll('.du-wheel__label');
      if (labels[index]) labels[index].classList.add('is-winning');

      var won = !!(prize && prize.code && prize.code.trim());
      var wrap = this.disc.closest('.du-wheel__disc-wrap');
      if (won && !this.reducedMotion) wrap.classList.add('is-celebrating');

      await this.wait(this.reducedMotion ? 100 : 1050);
      this.showResult(prize, false);
      this.spinning = false;
    }

    showResult(prize, isReplay) {
      var won = !!(prize && prize.code && prize.code.trim());

      this.stage.hidden  = true;
      this.result.hidden = false;
      this.result.querySelector('[data-result-mark]').textContent = won ? '★' : '↻';
      this.result.querySelector('[data-result-title]').textContent =
        won ? prize.label : (prize ? 'No luck this time' : 'Come back soon');
      this.result.querySelector('[data-result-body]').textContent =
        prize ? (prize.message || '') : '';

      var wrap = this.result.querySelector('[data-code-wrap]');
      if (won) {
        wrap.hidden = false;
        this.result.querySelector('[data-code]').textContent = prize.code;
        var btn = this.result.querySelector('[data-copy]');
        if (!btn.dataset.bound) {
          btn.dataset.bound = '1';
          btn.addEventListener('click', function () {
            var code = this.result.querySelector('[data-code]').textContent;
            navigator.clipboard.writeText(code).then(function () {
              btn.textContent = 'Copied';
              setTimeout(function () { btn.textContent = 'Copy'; }, 1800);
            }).catch(function () { btn.textContent = code; });
          }.bind(this));
        }
      } else {
        wrap.hidden = true;
      }

      var next = this.result.querySelector('[data-next]');
      var left = this.remaining();
      next.textContent = left > 0 ? 'You can spin again in ' + formatLeft(left) + '.' : '';
      this.result.classList.toggle('is-replay', !!isReplay);
    }
  });
})();
